import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Spin } from 'antd';
import { hasToken, setToken, setUserInfo, getUserInfo, clearAllUserData, redirectToLogin } from '@/utils/auth';
import AuthService from '@/services/amai/auth.service';
import { apiCache } from '@/utils';
import i18n from '@/i18n/config';
import { useUserBenefitsCheck } from '@/hooks/useUserBenefits';
import WelcomeGiftModal from '@/components/common/Modal/WelcomeGiftModal';
import PhoneVerificationRewardModal from '@/components/common/Modal/PhoneVerificationRewardModal';
// import ContinuousCreationRewardModal from '@/components/common/Modal/ContinuousCreationRewardModal'; // 暂时禁用每日登录奖励
import MonthlyRewardModal, { RewardType } from '@/components/common/Modal/MonthlyRewardModal';
import { eventBus } from '@/utils/eventBus';
import { usePhoneVerification } from '@/hooks/usePhoneVerification';
import PhoneLoginModal from '@/components/common/Modal/PhoneLogin/PhoneLoginModal';

/**
 * 路由守卫组件
 * 核心职责：
 * 1. 检查是否存在Token，若无则重定向到登录页。
 * 2. 若存在Ticket，则使用它来静默获取Token。
 * 3. 在认证检查期间显示加载状态。
 */
interface AuthGuardProps {
  children: React.ReactNode;
}
const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const [isPaymentReward, setIsPaymentReward] = useState(false); // 新增：标记是否为支付完成奖励
  // 站内登录弹窗（LoginModal）登录成功后自增，触发认证检查重跑，避免停在 loading
  const [loginRefresh, setLoginRefresh] = useState(0);
  const isRequestingRef = useRef(false);
  const processedTicketRef = useRef<string | null>(null);
  const benefitsCheckingRef = useRef(false); // 新增：防止重复权益检查
  const hasInitialCheckRef = useRef(false); // 新增：标记是否已完成初始检查
  console.log('又开始了');
  // 使用手机号验证Hook
  const { showPhoneModal, handlePhoneSuccess, handlePhoneCancel } = usePhoneVerification();

  // 用户权益检查Hook
  const {
    showWelcomeGift,
    // showDailyReward, // 暂时禁用每日登录奖励
    showMonthlyReward, // 每月登录奖励
    showPhoneVerificationReward, // 手机号验证完成奖励
    benefitsData, // 权益数据
    checkBenefits, // 检查用户权益
    checkPaymentRewards, // 检查支付完成奖励
    checkPhoneVerificationReward, // 检查手机号验证完成奖励
    closeWelcomeGift, // 关闭欢迎礼包弹窗
    closeMonthlyReward, // 关闭每月奖励弹窗
    closePhoneVerificationReward, // 关闭手机号验证完成奖励弹窗
  } = useUserBenefitsCheck();

  // 统一的权益检查函数，防止重复调用
  const performBenefitsCheck = useCallback(
    async (isPaymentReward = false) => {
      if (benefitsCheckingRef.current) {
        console.log('[AuthGuard] 权益检查进行中，跳过重复调用');
        return;
      }

      // 如果不是支付奖励且已经完成过初始检查，跳过
      if (!isPaymentReward && hasInitialCheckRef.current) {
        console.log('[AuthGuard] 已完成初始权益检查，跳过重复调用');
        return;
      }

      // 如果当前已经有弹窗显示，跳过检查避免重复弹窗
      if (showWelcomeGift || showMonthlyReward) {
        console.log('[AuthGuard] 已有弹窗显示，跳过权益检查', {
          showWelcomeGift,
          showMonthlyReward,
        });
        return;
      }

      try {
        benefitsCheckingRef.current = true;
        console.log('[AuthGuard] 开始权益检查', {
          isPaymentReward,
          isInitialCheck: !hasInitialCheckRef.current,
        });

        if (isPaymentReward) {
          console.log('[AuthGuard] 执行支付奖励检查');
          setIsPaymentReward(true);
          await checkPaymentRewards();
        } else {
          console.log('[AuthGuard] 执行常规权益检查');
          await checkBenefits();
          hasInitialCheckRef.current = true; // 标记已完成初始检查
        }
      } catch (error) {
        console.error('[AuthGuard] 权益检查失败:', error);
      } finally {
        benefitsCheckingRef.current = false;
      }
    },
    [checkBenefits, checkPaymentRewards, showWelcomeGift, showMonthlyReward]
  );

  // 检查是否需要显示权益弹窗 - 必须在所有其他Hook之前
  useEffect(() => {
    if (authChecked && hasToken()) {
      // 在认证完成后检查用户权益
      performBenefitsCheck(false);
    }
  }, [authChecked, performBenefitsCheck]);

  // 监听支付成功事件，触发权益检查
  useEffect(() => {
    const handleCheckUserBenefits = () => {
      if (authChecked && hasToken()) {
        console.log('[AuthGuard] 收到权益检查事件，重新检查用户权益');
        performBenefitsCheck(true); // 支付奖励检查
      }
    };

    // 监听权益检查事件
    const unsubscribe = eventBus.on('checkUserBenefits', handleCheckUserBenefits);

    return () => {
      unsubscribe();
    };
  }, [authChecked, performBenefitsCheck]);

  // 监听手机号验证完成事件，触发手机号验证完成权益检查
  useEffect(() => {
    const handleCheckPhoneVerificationReward = () => {
      if (authChecked && hasToken()) {
        console.log('[AuthGuard] 收到手机号验证完成权益检查事件');
        checkPhoneVerificationReward();
      }
    };

    // 监听手机号验证完成权益检查事件
    const unsubscribe = eventBus.on('checkPhoneVerificationReward', handleCheckPhoneVerificationReward);

    return () => {
      unsubscribe();
    };
  }, [authChecked, checkPhoneVerificationReward]);

  // 监听站内登录弹窗登录成功（userInfoUpdated 事件带新 token），重新执行认证检查
  useEffect(() => {
    const unsubscribe = eventBus.on('userInfoUpdated', () => {
      setLoginRefresh(n => n + 1);
    });
    return () => {
      unsubscribe();
    };
  }, []);

  // 认证检查Effect
  useEffect(() => {
    console.log('[AuthGuard] 开始检查认证...');
    const checkAuth = async () => {
      console.log('[AuthGuard] 开始认证检查，当前状态:', {
        isRequesting: isRequestingRef.current,
        processedTicket: processedTicketRef.current,
        timestamp: Date.now(),
        url: window.location.href,
        search: location.search,
      });

      const urlParams = new URLSearchParams(location.search);
      
      const ticketFromUrl = urlParams.get('ticket');
      
      const isAuthenticated = hasToken();
      console.log('[AuthGuard] 开始检查认证...', { ticketFromUrl, isAuthenticated });

      if (isRequestingRef.current) return;

      if (ticketFromUrl) {
        console.log('[AuthGuard] 在URL中发现Ticket，准备获取Token...');
        if (processedTicketRef.current === ticketFromUrl) {
          console.log('[AuthGuard] setAuthChecked');
          setAuthChecked(true);
          return;
        }

        isRequestingRef.current = true;
        setIsLoading(true);

        try {
          // 清除所有用户数据，但保留认证信息（因为马上要设置新的token）
          console.log('[AuthGuard] 换取ticket时清除所有用户数据');
          try {
            const { LocalStorageCleaner } = await import('@/utils/localStorageCleaner');
            LocalStorageCleaner.clearAllUserData({
              keepAuth: true,
              verbose: true,
            });
          } catch (error) {
            console.error('[AuthGuard] 清理localStorage时发生错误:', error);
          }

          apiCache.delete('user-profile');

          console.log('[AuthGuard] Token开始发送请求');
          const response = await AuthService.getTokenByTicket(ticketFromUrl);
          console.log('[AuthGuard] Token请求响应:', response);

          if (response.code === 0 && response.data?.token) {
            console.log('[AuthGuard] Token获取成功，设置用户信息。');
            setToken(response.data.token);
            setUserInfo({
              user_id: response.data.user_id,
              user_name: response.data.user_name,
              user_avatar: response.data.user_avatar,
              mobile: response.data.mobile, // 添加手机号字段
            });

            // 获取完整用户信息，将 show_new_user_gift 写入本地存储
            try {
              const profileRes = await AuthService.getUserProfile(true);
              if (profileRes.code === 0 && profileRes.data) {
                const currentInfo = getUserInfo() || {};
                setUserInfo({
                  ...currentInfo,
                  show_new_user_gift: profileRes.data.show_new_user_gift,
                });
              }
            } catch (profileError) {
              console.warn('[AuthGuard] 获取用户完整信息失败:', profileError);
            }

            const url = new URL(window.location.href);
            url.searchParams.delete('ticket');
            window.history.replaceState({}, '', url.toString());
            processedTicketRef.current = ticketFromUrl;
            setAuthChecked(true);

            // 用户获取token后，检查用户权益
            setTimeout(() => {
              performBenefitsCheck(false);
            }, 500); // 延迟500ms确保token设置完成
          } else {
            throw new Error(`获取token失败: ${response.message || '无效的响应'}`);
          }
        } catch (error) {
          console.error('[AuthGuard] 获取Token失败，跳转到登录页。', error);
          try {
            await clearAllUserData();
          } catch (clearError) {
            console.error('[AuthGuard] 清理用户数据失败:', clearError);
          }
          // 使用统一的登录跳转逻辑
          redirectToLogin(navigate);
          return;
        } finally {
          setIsLoading(false);
          isRequestingRef.current = false;
        }
        return;
      } else {
        setIsLoading(false);
      }

      if (!isAuthenticated) {
        console.log('[AuthGuard] 无Token，跳转到登录页。');
        // 使用统一的登录跳转逻辑，并保存当前路径用于登录后跳转
        // redirectToLogin(navigate, location.pathname + location.search);
        redirectToLogin(navigate);
        return;
      }

      console.log('[AuthGuard] 检查完成，用户已认证。');

      setAuthChecked(true);

      // 团队空间校验（V0.1）：启动/路由变化时比对 account_list，
      // 当前 teamId 不在列表（被移出/解散）→ 回落个人空间并提示
      try {
        const { spaceManager } = await import('@/services/space/spaceManager');
        const valid = await spaceManager.validateCurrentSpace(navigate);
        if (!valid) {
          const { message } = await import('antd');
          message.warning(i18n.t('space.teamRemoved'));
        }
      } catch (spaceError) {
        console.warn('[AuthGuard] 团队空间校验失败:', spaceError);
      }
    };

    checkAuth();
  }, [location.pathname, location.search, loginRefresh]);

  return (
    <>
      {isLoading || !authChecked ? (
        <div className="flex items-center justify-center min-h-screen">
          <Spin size="large" />
        </div>
      ) : (
        <>
          {/* 手机号补充弹窗 - 优先级最高 */}
          {showPhoneModal && (
            <PhoneLoginModal
              open={showPhoneModal}
              onCancel={handlePhoneCancel}
              onSuccess={handlePhoneSuccess}
            />
          )}

          {/* 首次登录欢迎礼包弹窗 */}
          {/* {showWelcomeGift && benefitsData && (
            <WelcomeGiftModal
              benefitsData={benefitsData}
              onClose={() => {
                closeWelcomeGift();
                // 欢迎礼包弹窗关闭后，延迟刷新米粒值以获取权益奖励
                setTimeout(() => {
                  eventBus.emit('refreshUserCredits');
                }, 500);
              }}
            />
          )} */}

          {/* 手机号验证完成权益弹窗 */}
          {showPhoneVerificationReward && benefitsData && (
            <PhoneVerificationRewardModal
              benefitsData={benefitsData}
              onClose={() => {
                closePhoneVerificationReward();
                // 手机号验证完成权益弹窗关闭后，延迟刷新米粒值以获取权益奖励
                setTimeout(() => {
                  eventBus.emit('refreshUserCredits');
                }, 500);
              }}
            />
          )}

          {/* 每日登录奖励弹窗 - 暂时禁用 */}
          {/* {showDailyReward && benefitsData && !needPhoneVerification && (
          <ContinuousCreationRewardModal
            benefitsData={benefitsData}
            onClose={() => {
              console.log('[AuthGuard] 关闭每日奖励弹窗');
              // 注意：时间戳记录已在弹窗显示时完成，这里只需要更新UI状态
              markDailyShown();
              checkBenefits(); // 重新检查状态
            }}
          />
        )} */}

          {/* 每月登录奖励弹窗 */}
          {showMonthlyReward && benefitsData && (
            <MonthlyRewardModal
              benefitsData={benefitsData}
              rewardType={isPaymentReward ? RewardType.PAYMENT_SUCCESS : RewardType.MONTHLY_LOGIN}
              onClose={() => {
                if (isPaymentReward) {
                  // 支付奖励弹窗关闭后，延迟刷新米粒值以获取权益奖励
                  setTimeout(() => {
                    eventBus.emit('refreshUserCredits');
                  }, 500);
                }
                closeMonthlyReward(); // 使用统一的关闭方法
                setIsPaymentReward(false); // 重置支付奖励标记
              }}
            />
          )}

          {children}
        </>
      )}
    </>
  );
};

export default AuthGuard;
